import { useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";

export interface OptimizedProfile {
  id: string;
  name: string | null;
  lastname: string | null;
  email: string | null;
  full_name: string;
}

export interface OptimizedSeller extends OptimizedProfile {
  // Additional seller-specific fields can be added here
}

export interface OptimizedProfileWithRole extends OptimizedProfile {
  role: string;
  is_active: boolean;
}

// Hook for fetching active sellers
export const useOptimizedSellers = () => {
  const [sellers, setSellers] = useState<OptimizedSeller[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSellers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc("get_active_sellers");

      if (error) {
        throw error;
      }

      setSellers(data || []);
    } catch (err) {
      console.error("Error fetching sellers:", err);
      setError("No se pudieron cargar los vendedores");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSellers();
  }, []);

  return { sellers, isLoading, error, refetch: fetchSellers };
};

// Hook for fetching profiles by IDs
export const useOptimizedProfiles = (userIds: string[]) => {
  const [profiles, setProfiles] = useState<Record<string, OptimizedProfile>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      // Filter out empty IDs and IDs we already have cached
      const idsToFetch = userIds.filter((id) => id && !profiles[id]);
      if (idsToFetch.length === 0) return;

      setIsLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase.rpc("get_profiles_by_ids", {
          p_user_ids: idsToFetch,
        });

        if (error) {
          throw error;
        }

        const newProfiles: Record<string, OptimizedProfile> = { ...profiles };

        data.forEach((profile) => {
          newProfiles[profile.id] = {
            id: profile.id,
            name: profile.name,
            lastname: profile.lastname,
            email: profile.email,
            full_name: profile.full_name,
          };
        });

        setProfiles(newProfiles);
      } catch (err) {
        console.error("Error fetching profiles:", err);
        setError("No se pudieron cargar los perfiles de usuarios");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, [userIds]);

  const getUserName = (userId: string | null | undefined): string => {
    if (!userId) return "Sistema";
    return profiles[userId]?.full_name || `${userId.substring(0, 6)}...`;
  };

  return { profiles, loading: isLoading, error, getUserName };
};

// Hook for fetching all profiles with roles (admin use)
export const useOptimizedProfilesWithRoles = () => {
  const [profiles, setProfiles] = useState<OptimizedProfileWithRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc("get_all_profiles_with_roles");

      if (error) {
        throw error;
      }

      setProfiles(data || []);
    } catch (err) {
      console.error("Error fetching profiles with roles:", err);
      setError("No se pudieron cargar los perfiles");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  return { profiles, isLoading, error, refetch: fetchProfiles };
};
